import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Layers, Plus, Download, RefreshCw, Trash2, Image, Filter, Search, Wand2, ChevronDown
} from 'lucide-react';
import { AdminPageWrapper } from '../components/admin/AdminPageWrapper';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminSlidesPage() {
  const { isLight } = useAdminTheme();
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({ product_id: '', format_type: '' });
  const [products, setProducts] = useState([]);
  const [atmospheres, setAtmospheres] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [regenerating, setRegenerating] = useState(null);

  const getToken = () => localStorage.getItem('admin_token');

  useEffect(() => {
    fetchSlides();
    fetchProducts();
    fetchAtmospheres();
  }, [filters]);

  const fetchSlides = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.product_id) params.append('product_id', filters.product_id);
      if (filters.format_type) params.append('format_type', filters.format_type);

      const response = await fetch(`${API_URL}/api/admin/slides?${params}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setSlides(data.slides);
        setStats(data.stats);
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/products?limit=100`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
      }
    } catch (error) {
      console.error('Products fetch error:', error);
    }
  };

  const fetchAtmospheres = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/atmospheres`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      if (response.ok) {
        const data = await response.json();
        setAtmospheres(data.atmospheres || []);
      }
    } catch (error) {
      console.error('Atmospheres fetch error:', error);
    }
  };

  const handleCreate = async (formData) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/slides`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail);

      toast.success('Slide créé');
      setShowCreateModal(false);
      fetchSlides();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer ce slide ?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/slides/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });

      if (response.ok) {
        toast.success('Slide supprimé');
        fetchSlides();
      }
    } catch (error) {
      toast.error('Erreur de suppression');
    }
  };

  const handleRegenerate = async (id) => {
    setRegenerating(id);
    try {
      const response = await fetch(`${API_URL}/api/admin/slides/${id}/regenerate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.detail);

      toast.success('Slide régénéré');
      fetchSlides();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRegenerating(null);
    }
  };

  const handleDownload = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/slides/${id}/download`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });

      if (!response.ok) throw new Error('Téléchargement impossible');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `slide_${id}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const formatLabels = {
    square: '1:1 Carré',
    story: '9:16 Story',
    banner: '16:9 Bannière',
    portrait: '4:5 Portrait',
  };

  return (
    <AdminPageWrapper title="Slide Manager" subtitle="Visuels marketing pour les produits">
      <div className="max-w-7xl mx-auto" data-testid="admin-slides-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className={cn("text-3xl font-bold flex items-center gap-3", isLight ? "text-gray-900" : "text-white")}>
              <Layers className="w-8 h-8 text-[#C0000C]" />
              Slide Manager
            </h1>
            <p className={isLight ? "text-gray-600" : "text-white"}>Visuels marketing pour les produits</p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-[#C0000C] hover:bg-[#A0000A] text-white"
            data-testid="create-slide-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau slide
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          {Object.entries(formatLabels).map(([key, label]) => (
            <div key={key} className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-white">{stats[key] || 0}</div>
              <div className="text-gray-200 text-sm">{label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <select
            value={filters.product_id}
            onChange={(e) => setFilters({...filters, product_id: e.target.value})}
            className="bg-zinc-900/50 border border-zinc-700 rounded-md px-3 py-2 text-white min-w-[200px]"
          >
            <option value="">Tous les produits</option>
            {products.map(p => (
              <option key={p.id} value={p.id}>{p.name?.fr || p.name?.en || p.id}</option>
            ))}
          </select>

          <select
            value={filters.format_type}
            onChange={(e) => setFilters({...filters, format_type: e.target.value})}
            className="bg-zinc-900/50 border border-zinc-700 rounded-md px-3 py-2 text-white"
          >
            <option value="">Tous les formats</option>
            {Object.entries(formatLabels).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>

          <Button
            variant="outline"
            onClick={fetchSlides}
            className="border-zinc-700 text-white hover:text-white"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-center py-20 text-gray-200">Chargement...</div>
        ) : slides.length === 0 ? (
          <div className="text-center py-20">
            <Image className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <p className="text-white">Aucun slide créé</p>
            <p className="text-zinc-600 text-sm">Créez des slides pour vos produits</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {slides.map(slide => (
              <div
                key={slide.id}
                className="bg-zinc-800/30 border border-zinc-700/50 rounded-xl overflow-hidden"
                data-testid={`slide-${slide.id}`}
              >
                {/* Image */}
                <div className="aspect-square bg-zinc-900 relative">
                  {slide.image_url ? (
                    <img
                      src={`${API_URL}${slide.image_url}`}
                      alt={slide.product_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-12 h-12 text-zinc-700" />
                    </div>
                  )}
                  <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    {formatLabels[slide.format_type] || slide.format_type}
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="text-white font-medium text-sm line-clamp-1">{slide.product_name}</h3>
                  {slide.atmosphere_name && (
                    <p className="text-gray-200 text-xs mt-1">{slide.atmosphere_name}</p>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 mt-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(slide.id)}
                      className="flex-1 border-zinc-700 text-white hover:text-white"
                      data-testid={`download-${slide.id}`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRegenerate(slide.id)}
                      disabled={regenerating === slide.id}
                      className="flex-1 border-zinc-700 text-white hover:text-white"
                    >
                      <RefreshCw className={`w-4 h-4 ${regenerating === slide.id ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(slide.id)}
                      className="border-red-600 text-red-500 hover:bg-red-600/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <CreateSlideModal
            products={products}
            atmospheres={atmospheres}
            formatLabels={formatLabels}
            onClose={() => setShowCreateModal(false)}
            onCreate={handleCreate}
          />
        )}
      </div>
    </AdminPageWrapper>
  );
}

function CreateSlideModal({ products, atmospheres, formatLabels, onClose, onCreate }) {
  const [formData, setFormData] = useState({
    product_id: '',
    atmosphere_id: '',
    format_type: 'square',
    custom_title: '',
    cta_text: 'SHOP NOW',
    benefits: [],
    custom_prompt: '',
  });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPromptEditor, setShowPromptEditor] = useState(false);

  // Prompts suggérés selon le format
  const promptSuggestions = {
    banner: [
      {
        label: "Slider Horizontal - Produit en vedette",
        prompt: "Wide horizontal banner for e-commerce slider featuring {product_name}. The product is the absolute hero, displayed prominently on the left side. Clean, minimal luxury background. Premium streetwear aesthetic. Professional product photography with dramatic lighting. Space on the right for text overlay. 16:9 ratio optimized for horizontal carousel display. High-end fashion brand visual."
      },
      {
        label: "Slider Horizontal - Style Lifestyle",
        prompt: "Cinematic horizontal banner showcasing {product_name} in a lifestyle context. Wide panoramic composition, product featured prominently. Urban luxury aesthetic, moody atmospheric lighting. Perfect for homepage slider, horizontal format with limited height. Fashion editorial quality."
      },
      {
        label: "Slider Horizontal - Minimaliste Premium",
        prompt: "Minimalist horizontal banner for {product_name}. Clean white/neutral background, product floating with subtle shadow. Ultra-premium aesthetic, Apple-style simplicity. Wide format optimized for web slider, product is the focal point. Elegant negative space for text."
      },
    ],
    square: [
      {
        label: "Instagram Feed - Produit Centré",
        prompt: "Square format product showcase for {product_name}. Centered composition, clean background, Instagram-ready. Professional e-commerce photography, luxury streetwear aesthetic. Perfect 1:1 ratio for social feed."
      },
      {
        label: "Carré - Style Editorial",
        prompt: "Fashion editorial square image featuring {product_name}. Magazine-quality visual, artistic composition, dramatic lighting. Premium brand positioning, high-end streetwear aesthetic."
      },
    ],
    story: [
      {
        label: "Story Instagram - Impact Maximum",
        prompt: "Vertical story format showcasing {product_name}. Mobile-optimized 9:16 ratio, scroll-stopping visual. Product prominent in center, space at top and bottom for text overlays. Social media story ready, vibrant and engaging."
      },
    ],
    portrait: [
      {
        label: "Portrait - Style Magazine",
        prompt: "Portrait format (4:5) featuring {product_name}. Fashion magazine aesthetic, editorial quality. Product beautifully framed, premium positioning. Instagram portrait post ready."
      },
    ],
  };

  // Filtrer les produits
  const filteredProducts = products.filter(p => {
    const productName = p.name?.fr || p.name?.en || '';
    return !searchQuery || productName.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.product_id) {
      toast.error('Sélectionnez un produit');
      return;
    }
    setLoading(true);
    await onCreate(formData);
    setLoading(false);
  };

  // Trouver le produit sélectionné
  const selectedProduct = products.find(p => p.id === formData.product_id);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <h2 className="text-xl font-bold text-white">Nouveau Slide</h2>
          <Button variant="ghost" onClick={onClose} className="text-white">×</Button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Sélection du produit avec vignettes */}
          <div>
            <label className="text-white text-sm block mb-2">Produit * (cliquez pour sélectionner)</label>
            
            {/* Barre de recherche */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher un produit..."
                className="bg-zinc-800 border-zinc-700 pl-10"
              />
            </div>
            
            {/* Produit sélectionné */}
            {selectedProduct && (
              <div className="mb-3 p-2 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center gap-3">
                {(() => {
                  let imgUrl = null;
                  if (selectedProduct.images) {
                    if (Array.isArray(selectedProduct.images)) {
                      imgUrl = selectedProduct.images[0];
                    } else if (selectedProduct.images.main?.length > 0) {
                      imgUrl = selectedProduct.images.main[0];
                    }
                  }
                  const fullImgUrl = imgUrl?.startsWith('/api/') ? `${API_URL}${imgUrl}` : imgUrl;
                  return fullImgUrl ? (
                    <img src={fullImgUrl} alt="" className="w-12 h-12 object-cover rounded" />
                  ) : (
                    <div className="w-12 h-12 bg-zinc-800 rounded flex items-center justify-center">
                      <Image className="w-6 h-6 text-zinc-600" />
                    </div>
                  );
                })()}
                <div className="flex-1">
                  <p className="text-white font-medium">{selectedProduct.name?.fr || selectedProduct.name?.en}</p>
                  <p className="text-green-400 text-xs">Sélectionné</p>
                </div>
              </div>
            )}
            
            {/* Grille de produits avec vignettes */}
            <div className="grid grid-cols-4 md:grid-cols-6 gap-2 max-h-48 overflow-y-auto p-2 bg-zinc-800/30 rounded-lg border border-zinc-700">
              {filteredProducts.slice(0, 30).map(p => {
                const isSelected = formData.product_id === p.id;
                let imgUrl = null;
                if (p.images) {
                  if (Array.isArray(p.images)) {
                    imgUrl = p.images[0];
                  } else if (p.images.main?.length > 0) {
                    imgUrl = p.images.main[0];
                  }
                }
                const fullImgUrl = imgUrl?.startsWith('/api/') ? `${API_URL}${imgUrl}` : imgUrl;
                const productName = p.name?.fr || p.name?.en || 'Sans nom';
                
                return (
                  <div
                    key={p.id}
                    onClick={() => setFormData({...formData, product_id: p.id})}
                    className={cn(
                      "cursor-pointer rounded-lg border-2 transition-all overflow-hidden",
                      isSelected 
                        ? "border-green-500 ring-2 ring-green-500/30 bg-green-500/10" 
                        : "border-zinc-700 hover:border-zinc-500"
                    )}
                  >
                    <div className="aspect-square bg-zinc-800 overflow-hidden">
                      {fullImgUrl ? (
                        <img 
                          src={fullImgUrl} 
                          alt={productName} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="w-6 h-6 text-zinc-600" />
                        </div>
                      )}
                    </div>
                    <p className="text-[10px] text-white truncate p-1 text-center">
                      {productName.substring(0, 15)}
                    </p>
                    {isSelected && (
                      <div className="bg-green-500 text-white text-[10px] py-0.5 text-center">
                        ✓
                      </div>
                    )}
                  </div>
                );
              })}
              {filteredProducts.length === 0 && (
                <div className="col-span-full text-center py-8 text-gray-400">
                  <Image className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>{searchQuery ? 'Aucun produit trouvé' : 'Chargement...'}</p>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="text-white text-sm block mb-1">Atmosphère</label>
            <select
              value={formData.atmosphere_id}
              onChange={(e) => setFormData({...formData, atmosphere_id: e.target.value})}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-white"
            >
              <option value="">Par défaut</option>
              {atmospheres.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-white text-sm block mb-1">Format</label>
            <select
              value={formData.format_type}
              onChange={(e) => setFormData({...formData, format_type: e.target.value})}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-white"
            >
              {Object.entries(formatLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-white text-sm block mb-1">Texte CTA</label>
            <Input
              value={formData.cta_text}
              onChange={(e) => setFormData({...formData, cta_text: e.target.value})}
              placeholder="SHOP NOW"
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>

          {/* Prompt IA Personnalisé */}
          <div className="border border-zinc-700 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowPromptEditor(!showPromptEditor)}
              className="w-full flex items-center justify-between p-3 bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-purple-400" />
                <span className="text-white text-sm font-medium">Prompt IA personnalisé</span>
                {formData.custom_prompt && (
                  <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">Actif</span>
                )}
              </div>
              <ChevronDown className={cn("w-4 h-4 text-gray-400 transition-transform", showPromptEditor && "rotate-180")} />
            </button>
            
            {showPromptEditor && (
              <div className="p-4 space-y-3 bg-zinc-900/50">
                <p className="text-xs text-gray-400">
                  Personnalisez le prompt envoyé à l'IA pour générer l'image. Utilisez <code className="bg-zinc-800 px-1 rounded">{'{product_name}'}</code> pour insérer le nom du produit.
                </p>
                
                {/* Suggestions de prompts */}
                {promptSuggestions[formData.format_type]?.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-400">Suggestions pour {formatLabels[formData.format_type]} :</Label>
                    <div className="flex flex-wrap gap-2">
                      {promptSuggestions[formData.format_type].map((suggestion, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setFormData({...formData, custom_prompt: suggestion.prompt})}
                          className={cn(
                            "text-xs px-3 py-1.5 rounded-full border transition-colors",
                            formData.custom_prompt === suggestion.prompt
                              ? "bg-purple-500/20 border-purple-500 text-purple-400"
                              : "border-zinc-700 text-gray-300 hover:border-purple-500/50 hover:text-purple-400"
                          )}
                        >
                          {suggestion.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                <Textarea
                  value={formData.custom_prompt}
                  onChange={(e) => setFormData({...formData, custom_prompt: e.target.value})}
                  placeholder="Ex: Wide horizontal banner for e-commerce slider featuring {product_name}. Product prominently displayed, luxury aesthetic..."
                  className="bg-zinc-800 border-zinc-700 text-white min-h-[100px] text-sm"
                />
                
                {formData.custom_prompt && (
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, custom_prompt: ''})}
                    className="text-xs text-red-400 hover:text-red-300"
                  >
                    ✕ Effacer le prompt personnalisé
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-zinc-700 text-white"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#C0000C] hover:bg-[#A0000A] text-white"
              data-testid="slide-create-submit"
            >
              {loading ? 'Création...' : 'Créer le slide'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminSlidesPage;
